import { InfoPopover, InfoPopoverBody, InfoPopoverFooter } from 'components/InfoPopover/InfoPopover';
import { PaymentReconciliationDetailsData } from 'pages/PaymentReconciliation/PaymentReconciliationDetails/types';
import { CSSProperties, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { EMPTY_FIELD } from 'support/helpers/const/const';
import { formatCurrency } from 'support/helpers/currency/currency';

type PaymentReconciliationLineItem = PaymentReconciliationDetailsData['data']['line_items'][number];

export const DiscrepanciesModal = forwardRef<
  HTMLDivElement,
  {
    corrections: PaymentReconciliationLineItem['corrections'];
    dueAmount: number;
    remittedAmount: number;
    currency: string | undefined | null;
    style?: CSSProperties;
  }
>(({ style, corrections, currency, dueAmount, remittedAmount }, ref) => {
  const { t } = useTranslation();
  return (
    <InfoPopover ref={ref} style={style}>
      <InfoPopoverBody>
        <h1 className="text-sm font-bold leading-tight text-gray-900">
          {t('paymentReconciliation:detailPage.correctionsModal.title')}
        </h1>
        <div className="space-y-5 pb-2 pt-5">
          <div className="grid w-full grid-cols-2">
            <div className="text-xs text-gray-500">
              {t('paymentReconciliation:detailPage.correctionsModal.dueAmount')}
            </div>
            <div className="text-end text-xs text-gray-900">{formatCurrency(dueAmount, currency)}</div>
          </div>
          {corrections?.map((correction) => (
            <DiscrepancyItem
              totalAmount={dueAmount}
              key={correction.reason}
              amount={correction.correction_amount ?? 0}
              reason={correction.reason || ''}
              currency={currency}
            />
          ))}
        </div>
      </InfoPopoverBody>
      <InfoPopoverFooter>
        <div className="flex w-full items-center justify-between p-2">
          <div className="text-xs text-gray-500">
            {t('paymentReconciliation:detailPage.correctionsModal.footer.title')}
          </div>
          <div className="text-xs font-semibold text-gray-900">{formatCurrency(remittedAmount, currency)}</div>
        </div>
      </InfoPopoverFooter>
    </InfoPopover>
  );
});

DiscrepanciesModal.displayName = 'CorrectionsModal';

const formatPercentage = (value: number) => {
  return new Intl.NumberFormat('default', {
    style: 'percent',
    maximumFractionDigits: 2,
  }).format(value);
};

const DiscrepancyItem = ({
  totalAmount,
  amount,
  reason,
  currency,
}: {
  totalAmount: number;
  amount: number;
  reason: string;
  currency: string | undefined | null;
}) => {
  const percentage = totalAmount === 0 ? 0 : amount / totalAmount;
  return (
    <div>
      <div className="grid w-full grid-cols-2">
        <div className="text-xs text-gray-500">{reason || EMPTY_FIELD}</div>
        <div className="flex items-center justify-end gap-2.5 text-xs">
          {percentage ? (
            <>
              <span className="text-red-700">{formatPercentage(percentage)}</span>
              <span className="text-gray-300">/</span>
            </>
          ) : null}
          <div className="text-end text-gray-900">{formatCurrency(amount, currency) || EMPTY_FIELD}</div>
        </div>
      </div>
    </div>
  );
};
